import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ThemeProvider } from '@mui/material/styles';
import { Box, CircularProgress, CssBaseline, Paper, Typography } from '@mui/material';
import quailGameTheme from './theme/quailGameTheme';

// Global styles
import './styles/pageHeaders.css';

import Home from './pages/Home';
import Teams from './pages/Teams';
import League from './pages/League';
import Standings from './pages/Standings';
import SchedulePage from './pages/SchedulePage';
import TeamDetail from './pages/TeamDetail';
import Venues from './pages/Venues';
import Spares from './pages/Spares';
import LeagueDraft from './pages/LeagueDraft';
import UploadPage from './pages/UploadPage';
import RsvpResponsePage from './pages/public/RsvpResponsePage';
import SpareAvailabilityResponsePage from './pages/public/SpareAvailabilityResponsePage';
import PlayerAvailabilityResponsePage from './pages/public/PlayerAvailabilityResponsePage';
import PhotoUploadPage from './pages/public/PhotoUploadPage';
import EmergencyContactPage from './pages/public/EmergencyContactPage';
import RegistrationPage from './pages/public/RegistrationPage';
import RegistrationCallbackPage from './pages/public/RegistrationCallbackPage';
import RegistrationLandingPage from './pages/public/RegistrationLandingPage';
import EventRegistrationPage from './pages/public/EventRegistrationPage';
import EventRegistrationCallbackPage from './pages/public/EventRegistrationCallbackPage';
import PlayerProfile from './pages/PlayerProfile';
import ChatPage from './pages/ChatPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

import GameForm from './pages/game/GameForm';
import ScoreFormRedirect from './pages/game/ScoreFormRedirect';
import SubmitMatchReport from './pages/SubmitMatchReport.tsx';
import PendingMatchReports from './pages/PendingMatchReports';

// Admin pages
import OrganizationAdminDashboard from './pages/admin/organization/OrganizationAdminDashboard';
import LeagueAdminDashboard from './pages/admin/league/LeagueAdminDashboard';
import LeagueAttendanceReport from './pages/admin/league/LeagueAttendanceReport';
import LeagueDisciplinaryReport from './pages/admin/league/LeagueDisciplinaryReport';
import LeagueProfilePhotosPage from './pages/admin/league/LeagueProfilePhotosPage';
import LeaguesManagePage from './pages/admin/organization/LeaguesManagePage';
import LeagueForm from './components/league/LeagueForm';
import OrganizationMembersPage from './pages/admin/organization/OrganizationMembersPage';
import PlayerAvailabilityPage from './pages/admin/league/PlayerAvailabilityPage';
import LeagueSettingsPage from './pages/admin/league/LeagueSettingsPage';
import LeagueMembersPage from './pages/admin/league/LeagueMembersPage';
import RegistrationConfigPage from './pages/admin/league/RegistrationConfigPage';
import RegistrationDashboardPage from './pages/admin/league/RegistrationDashboardPage';
import ReminderEmailDashboardPage from './pages/admin/league/ReminderEmailDashboardPage';
import MatchReportConflictsPage from './pages/admin/league/MatchReportConflictsPage';
import LeagueMatchReportsPage from './pages/admin/league/LeagueMatchReportsPage';
import PlayoffConfigPage from './pages/admin/league/PlayoffConfigPage';
import TeamManagementPage from './pages/admin/league/TeamManagementPage';
import ScheduleGeneratorListPage from './pages/admin/league/ScheduleGeneratorListPage';
import ScheduleDraftPage from './pages/admin/league/ScheduleDraftPage';
import MergeUsersPage from './pages/admin/organization/MergeUsersPage';
import ComposeEmailPage from './pages/admin/organization/ComposeEmailPage';
import ManualGameReminderPage from './pages/admin/organization/ManualGameReminderPage';
import OutstandingBalancesPage from './pages/admin/organization/OutstandingBalancesPage';
import ChatAdminPage from './pages/admin/chat/ChatAdminPage';
import VenueManagementPage from './pages/admin/organization/VenueManagementPage';
import EventListPage from './pages/admin/events/EventListPage';
import EventFormPage from './pages/admin/events/EventFormPage';
import EventRegistrationDashboardPage from './pages/admin/events/EventRegistrationDashboardPage';
import ReportsHubPage from './pages/admin/reports/ReportsHubPage';
import SeasonComparisonPage from './pages/admin/reports/SeasonComparisonPage';
import SocialEventListPage from './pages/admin/socialEvents/SocialEventListPage';
import SocialEventFormPage from './pages/admin/socialEvents/SocialEventFormPage';
import SocialEventRsvpsPage from './pages/admin/socialEvents/SocialEventRsvpsPage';
import SocialEventRsvpPage from './pages/public/SocialEventRsvpPage';

// Auth pages
import CallbackPage from './pages/auth/CallbackPage.jsx';
import AcceptInvitePage from './pages/auth/AcceptInvitePage.jsx';
import LoginHub from './pages/auth/LoginHub.jsx';

import { useAuth } from './contexts/AuthContext';
import { usePermissions } from './contexts/PermissionsContext';
import Layout from './layouts/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SEOMetadata from './components/SEOMetadata';

import './App.css';

const SocialEventsPermissionGate = ({ children, requireManage = false }) => {
  const { isLoading, canViewSocialEvents, canManageSocialEvents } = usePermissions();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const allowed = requireManage ? canManageSocialEvents() : canViewSocialEvents();
  if (!allowed) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', py: 6, px: 2 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>Access denied</Typography>
          <Typography color="text.secondary">You do not have permission to view this page.</Typography>
        </Paper>
      </Box>
    );
  }

  return children;
};

const SocialEventsProtectedRoute = ({ children, requireManage = false }) => (
  <ProtectedRoute>
    <SocialEventsPermissionGate requireManage={requireManage}>
      {children}
    </SocialEventsPermissionGate>
  </ProtectedRoute>
);

function App() {
  const { isLoading } = useAuth();
  
  // Show loading spinner while Auth0 is initializing
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column' 
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Standard app routing for all subdomains
  return (
    <ThemeProvider theme={quailGameTheme}>
      <CssBaseline />
      <SEOMetadata />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Router>
          <Layout>
          <Routes>
            {/* Auth routes */}
            <Route path='/callback' element={<CallbackPage />} />
            <Route path='/accept-invite' element={<AcceptInvitePage />} />
            <Route path='/login' element={<LoginHub />} />
            
            {/* Public Routes - No authentication required */}
            <Route path='/leagues/:leagueSlug' element={<League />} />
            <Route path='/leagues/:leagueSlug/standings' element={<Standings />} />
            <Route path='/leagues/:leagueSlug/schedule' element={<SchedulePage />} />
            <Route path='/leagues/:leagueSlug/teams' element={<Teams />} />
            <Route path='/leagues/:leagueSlug/spares' element={<Spares />} />
            <Route
              path='/leagues/:leagueSlug/draft'
              element={
                <ProtectedRoute>
                  <LeagueDraft />
                </ProtectedRoute>
              }
            />
            <Route path='/leagues/:leagueSlug/register' element={<RegistrationPage />} />

            {/* Team chat */}
            <Route
              path='/chat'
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path='/chat/:channelId'
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route path='/leagues/:leagueSlug/register/callback' element={<RegistrationCallbackPage />} />
            <Route path='/register' element={<RegistrationLandingPage />} />
            <Route path='/events/:eventSlug/register' element={<EventRegistrationPage />} />
            <Route path='/events/:eventSlug/register/callback' element={<EventRegistrationCallbackPage />} />
            <Route path='/teams/:teamSlug' element={<TeamDetail />} />
            <Route path='/venues' element={<Venues />} />
            
            {/* Player Profile - Authenticated only */}
            <Route 
              path='/player/:userTenantId' 
              element={
                <ProtectedRoute>
                  <PlayerProfile />
                </ProtectedRoute>
              } 
            />
            
            {/* public / root */}
            <Route path='/' element={<Home />} />
            <Route path='/home' element={<Home />} />
            <Route path='/teams' element={<Teams />} />
            <Route
              path='/rsvp/:token/:choice'
              element={<RsvpResponsePage />}
            />
            <Route
              path='/spare/availability/:token'
              element={<SpareAvailabilityResponsePage />}
            />
            <Route
              path='/player-availability/:token'
              element={<PlayerAvailabilityResponsePage />}
            />
            <Route
              path='/social-event-rsvp/:token'
              element={<SocialEventRsvpPage />}
            />
            <Route
              path='/photo-upload/:token'
              element={<PhotoUploadPage />}
            />
            <Route
              path='/emergency-contact/:token'
              element={<EmergencyContactPage />}
            />
            
            {/* Legal pages */}
            <Route path='/privacy' element={<PrivacyPolicy />} />
            <Route path='/terms' element={<TermsOfService />} />

            {/* Redirect old /public routes to home */}
            <Route path='/public/*' element={<Navigate to="/" replace />} />

            {/* Legacy organization URLs */}
            <Route
              path='/organization/*'
              element={<Navigate to="/" replace />}
            />

            {/* Directory pages removed (legacy organization views) */}

            {/* Legacy league routes removed */}
            
            {/* Legacy team dashboard routes removed */}
            
            {/* Submit Match Report */}
            <Route
              path='/organization/:orgId/league/:leagueId/team/:teamId/game/:gameId/report'
              element={
                <ProtectedRoute>
                  <SubmitMatchReport />
                </ProtectedRoute>
              }
            />
            
            {/* Legacy team & player management routes removed */}

            {/* Legacy venue & field management routes removed */}

            {/* Game management */}
            <Route
              path='/organization/:orgId/league/:leagueId/game/create'
              element={
                <ProtectedRoute>
                  <GameForm />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organization/:orgId/league/:leagueId/game/:gameId/edit'
              element={
                <ProtectedRoute>
                  <GameForm />
                </ProtectedRoute>
              }
            />
            <Route
              path='/organization/:orgId/league/:leagueId/game/:gameId/scores'
              element={
                <ProtectedRoute>
                  <ScoreFormRedirect />
                </ProtectedRoute>
              }
            />
            
            {/* Simplified Game Routes */}
            <Route
              path='/games/:gameId/edit'
              element={
                <ProtectedRoute>
                  <GameForm />
                </ProtectedRoute>
              }
            />
            <Route
              path='/games/:gameId/teams/:teamId/report'
              element={
                <ProtectedRoute>
                  <SubmitMatchReport />
                </ProtectedRoute>
              }
            />
            <Route
              path='/games/:gameId/teams/:teamId/report/edit'
              element={
                <ProtectedRoute>
                  <SubmitMatchReport />
                </ProtectedRoute>
              }
            />
            <Route
              path='/match-reports/pending'
              element={
                <ProtectedRoute>
                  <PendingMatchReports />
                </ProtectedRoute>
              }
            />

            {/* Admin‐only */}
            <Route 
              path='/upload' 
              element={
                <ProtectedRoute requiredRole="admin">
                  <UploadPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Admin Routes */}
            <Route 
              path='/admin' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <OrganizationAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/organization/:orgId' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <OrganizationAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeaguesManagePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/organization/:orgId/leagues' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeaguesManagePage />
                </ProtectedRoute>
              } 
            />            
            {/* Legacy admin league creation route removed */}
            <Route 
              path='/admin/leagues/create' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeagueForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeagueAdminDashboard />
                </ProtectedRoute>
              } 
            />
            {/* Legacy admin league edit routes removed */}
            <Route 
              path='/admin/leagues/:leagueId/settings' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeagueSettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/members' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeagueMembersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/members' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <OrganizationMembersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/merge-users' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <MergeUsersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/game-reminders' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <ManualGameReminderPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/chat'
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <ChatAdminPage />
                </ProtectedRoute>
              }
            />
            <Route path='/admin/chat-channels' element={<Navigate to='/admin/chat' replace />} />
            <Route 
              path='/admin/outstanding-balances' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <OutstandingBalancesPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/compose-email' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <ComposeEmailPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/venues' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <VenueManagementPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/events' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <EventListPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/events/new' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <EventFormPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/events/:eventId/edit' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <EventFormPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path='/admin/social-events'
              element={
                <SocialEventsProtectedRoute>
                  <SocialEventListPage />
                </SocialEventsProtectedRoute>
              }
            />
            <Route
              path='/admin/social-events/new'
              element={
                <SocialEventsProtectedRoute requireManage>
                  <SocialEventFormPage />
                </SocialEventsProtectedRoute>
              }
            />
            <Route
              path='/admin/social-events/:id/edit'
              element={
                <SocialEventsProtectedRoute requireManage>
                  <SocialEventFormPage />
                </SocialEventsProtectedRoute>
              }
            />
            <Route
              path='/admin/social-events/:id/rsvps'
              element={
                <SocialEventsProtectedRoute>
                  <SocialEventRsvpsPage />
                </SocialEventsProtectedRoute>
              }
            />
            <Route 
              path='/admin/events/:eventId/registrations' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <EventRegistrationDashboardPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/reports' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <ReportsHubPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/reports/season-comparison' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <SeasonComparisonPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/organization/:orgId/members' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <OrganizationMembersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/organization/:orgId/league/:leagueId' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeagueAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/availability' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <PlayerAvailabilityPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/attendance' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeagueAttendanceReport />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/disciplinary' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeagueDisciplinaryReport />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/profile-photos' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeagueProfilePhotosPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/registration-config' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <RegistrationConfigPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/registrations' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <RegistrationDashboardPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/reminder-emails' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <ReminderEmailDashboardPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/match-report-conflicts' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <MatchReportConflictsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/match-reports' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <LeagueMatchReportsPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/playoffs' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <PlayoffConfigPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/teams' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <TeamManagementPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/schedule-generator' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <ScheduleGeneratorListPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path='/admin/leagues/:leagueId/schedule-generator/:draftId' 
              element={
                <ProtectedRoute requiredPermission="admin:tenant">
                  <ScheduleDraftPage />
                </ProtectedRoute>
              } 
            />

            {/* Catch-all: redirect unknown routes to home */}
            <Route path='*' element={<Navigate to="/" replace />} />
          </Routes>
        </Layout>
      </Router>
    </LocalizationProvider>
    </ThemeProvider>
  );
}

export default App;
